import {
    Alert,
    Badge,
    Button,
    Card,
    Group,
    Loader,
    Modal,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {createFileRoute, useParams} from '@tanstack/react-router'
import {useState} from 'react'

import {deactivateUser, editUser, getUserDetail, reactivateUser} from 'src/api/admin/api'
import type {UserDetail} from 'src/api/admin/models/user'

export const Route = createFileRoute('/_authed/admin/users/$userId')({
    component: AdminUserDetailPage,
})

function AdminUserDetailPage() {
    const {userId} = useParams({from: '/_authed/admin/users/$userId'})
    const queryClient = useQueryClient()
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [deactivateModalOpen, setDeactivateModalOpen] = useState(false)
    const [reactivateModalOpen, setReactivateModalOpen] = useState(false)

    const {data: user, isLoading} = useQuery({
        queryKey: ['admin', 'users', userId],
        queryFn: () => getUserDetail(userId),
    })

    const editMutation = useMutation({
        mutationFn: (body: {name: string; email: string}) => editUser(userId, body),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin', 'users']})
            queryClient.invalidateQueries({
                queryKey: ['admin', 'users', userId],
            })
            setEditModalOpen(false)
            notifications.show({color: 'green', message: 'User updated'})
        },
        onError: (err: Error) => {
            notifications.show({color: 'red', message: err.message})
        },
    })

    const deactivateMutation = useMutation({
        mutationFn: () => deactivateUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin', 'users']})
            queryClient.invalidateQueries({
                queryKey: ['admin', 'users', userId],
            })
            setDeactivateModalOpen(false)
            notifications.show({color: 'green', message: 'User deactivated'})
        },
        onError: (err: Error) => {
            notifications.show({color: 'red', message: err.message})
        },
    })

    const reactivateMutation = useMutation({
        mutationFn: () => reactivateUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin', 'users']})
            queryClient.invalidateQueries({
                queryKey: ['admin', 'users', userId],
            })
            setReactivateModalOpen(false)
            notifications.show({color: 'green', message: 'User reactivated'})
        },
        onError: (err: Error) => {
            notifications.show({color: 'red', message: err.message})
        },
    })

    if (isLoading || !user) {
        return <Loader size="lg" style={{display: 'block', margin: '100px auto'}} />
    }

    return (
        <Stack gap="lg">
            {user.deactivatedAt && <Alert color="red" title="This user has been deactivated." />}
            <Title order={3}>{user.name}</Title>

            <Card withBorder padding="md">
                <Card.Section withBorder inheritPadding py="sm">
                    <Text fw={500}>User Info</Text>
                </Card.Section>
                <Card.Section inheritPadding pt="sm">
                    <Stack gap="xs">
                        <Text size="sm">
                            <Text span fw={500}>
                                Email:
                            </Text>{' '}
                            {user.email}
                        </Text>
                        <Group gap="xs" align="center">
                            <Text size="sm">
                                <Text span fw={500}>
                                    Role:
                                </Text>
                            </Text>
                            <Badge color={user.isAdmin ? 'blue' : 'gray'}>
                                {user.isAdmin ? 'Admin' : 'Client'}
                            </Badge>
                        </Group>
                        {user.organizationName && (
                            <Text size="sm">
                                <Text span fw={500}>
                                    Organization:
                                </Text>{' '}
                                {user.organizationName}
                            </Text>
                        )}
                        <Group gap="xs" align="center">
                            <Text size="sm">
                                <Text span fw={500}>
                                    Status:
                                </Text>
                            </Text>
                            <Badge color={user.deactivatedAt ? 'red' : 'green'}>
                                {user.deactivatedAt ? 'Deactivated' : 'Active'}
                            </Badge>
                        </Group>
                        <Text size="sm">
                            <Text span fw={500}>
                                Last active:
                            </Text>{' '}
                            {user.lastActiveAt
                                ? new Date(user.lastActiveAt).toLocaleString()
                                : 'Never'}
                        </Text>
                        <Group mt="sm">
                            <Button
                                variant="subtle"
                                size="compact-sm"
                                onClick={() => setEditModalOpen(true)}
                            >
                                Edit
                            </Button>
                            {user.deactivatedAt ? (
                                <Button
                                    variant="subtle"
                                    size="compact-sm"
                                    onClick={() => setReactivateModalOpen(true)}
                                >
                                    Reactivate
                                </Button>
                            ) : (
                                <Button
                                    variant="subtle"
                                    color="red"
                                    size="compact-sm"
                                    onClick={() => setDeactivateModalOpen(true)}
                                >
                                    Deactivate
                                </Button>
                            )}
                        </Group>
                    </Stack>
                </Card.Section>
            </Card>

            <Modal title="Edit User" opened={editModalOpen} onClose={() => setEditModalOpen(false)}>
                <EditUserForm
                    user={user}
                    onSubmit={(values) => editMutation.mutate(values)}
                    onCancel={() => setEditModalOpen(false)}
                    loading={editMutation.isPending}
                />
            </Modal>

            <Modal
                title="Deactivate User"
                opened={deactivateModalOpen}
                onClose={() => setDeactivateModalOpen(false)}
            >
                <Stack>
                    <Text size="sm">
                        Are you sure you want to deactivate {user.name}? They will be logged out
                        immediately.
                    </Text>
                    <Group>
                        <Button variant="default" onClick={() => setDeactivateModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            loading={deactivateMutation.isPending}
                            onClick={() => deactivateMutation.mutate()}
                        >
                            Deactivate
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal
                title="Reactivate User"
                opened={reactivateModalOpen}
                onClose={() => setReactivateModalOpen(false)}
            >
                <Stack>
                    <Text size="sm">
                        Are you sure you want to reactivate {user.name}? They will be able to log in
                        again.
                    </Text>
                    <Group>
                        <Button variant="default" onClick={() => setReactivateModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            loading={reactivateMutation.isPending}
                            onClick={() => reactivateMutation.mutate()}
                        >
                            Reactivate
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    )
}

type EditUserFormProps = {
    user: UserDetail
    onSubmit: (values: {name: string; email: string}) => void
    onCancel: () => void
    loading: boolean
}

function EditUserForm({user, onSubmit, onCancel, loading}: EditUserFormProps) {
    const form = useForm({
        initialValues: {name: user.name, email: user.email},
        validate: {
            name: (value) => (value?.trim() ? null : 'Enter a name'),
            email: (value) =>
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '') ? null : 'Enter a valid email',
        },
    })

    return (
        <form onSubmit={form.onSubmit(onSubmit)}>
            <Stack>
                <TextInput label="Name" placeholder="Full name" {...form.getInputProps('name')} />
                <TextInput
                    label="Email"
                    placeholder="you@example.com"
                    {...form.getInputProps('email')}
                />
                <Group>
                    <Button variant="default" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" loading={loading}>
                        Save
                    </Button>
                </Group>
            </Stack>
        </form>
    )
}
